import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkOrdersTable } from './components/work-orders/WorkOrdersTable';
import { PlansDetails } from './components/plans/PlansDetails';
import { maintPlanService } from './services/maintPlanService';
import { workOrdersService } from './services/workOrdersService';
import { WorkOrdersModal } from './components/work-orders/WorkOrdersModal';
import { Loader2, AlertCircle } from 'lucide-react';
import { useFactory } from '../../context/FactoryContext';


const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10];

const PlanesAsignacion = () => {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [filterAsset, setFilterAsset] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterMaintenanceType, setFilterMaintenanceType] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedWoId, setSelectedWoId] = useState(null);
    const [selectedWO, setSelectedWO] = useState(null);
    const [woToDelete, setWoToDelete] = useState(null);
    const { selectedFactory, factories, factoryCodes } = useFactory();

    // Work Orders specific state
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [searchQueryWO, setSearchQueryWO] = useState('');
    const [filterAssetWO, setFilterAssetWO] = useState('');
    const [filterPriorityWO, setFilterPriorityWO] = useState('');
    const [filterMaintenanceTypeWO, setFilterMaintenanceTypeWO] = useState('');
    const [filterStatusWO, setFilterStatusWO] = useState('');
    const [woPage, setWoPage] = useState(1);
    const [woPageSize, setWoPageSize] = useState(DEFAULT_PAGE_SIZE);

    // Fetch Maintenance Plans so we can filter WOs by Maintenance Plans
    const {
        data: plansResponse,
        isLoading: isLoadingPlans,
        isError: isErrorPlans,
        error: plansError
    } = useQuery({
        queryKey: ['plans', selectedFactory],
        queryFn: () => maintPlanService.getAll({ factory_code: selectedFactory }, 1, 1000),
        retry: 1,
    });
    const plans2 = plansResponse?.data || [];

    // Fetch ALL (1000) work orders from selected factory
    const {
        data: workOrdersResponse,
        isLoading: isLoadingWorkOrders,
        isError: isErrorWorkOrders,
        error: workOrdersError,
        refetch: refetchWorkOrders
    } = useQuery({
        queryKey: ['workOrders', selectedFactory],
        queryFn: () => workOrdersService.list({ factory_name: selectedFactory }, 1, 1000),
        retry: 1,
    });

    // Fetch one single Work Order when selected WoId is truthy value.
    const {
        data: woResponse,
        isLoading: isLoadingWo,
        isError: isErrorWo,
        error: woError
    } = useQuery({
        queryKey: ['wo', selectedWoId],
        queryFn: () => workOrdersService.getById(selectedWoId),
        retry: 1,
        enabled: !!selectedWoId,
    });

    // Update selected Work Order (not just ID).
    useEffect(() => {
        if (woResponse) {
            setSelectedWO(woResponse);
        }
    }, [woResponse]);

    const allWorkOrders = workOrdersResponse?.data || [];

    // Filter work orders based on selected plan and filters
    const filteredWorkOrders = useMemo(() => {
        let filtered = allWorkOrders;

        // Filter by selected plan
        if (selectedPlanId) {
            filtered = filtered.filter(wo => wo.maintenance_plan_id === parseInt(selectedPlanId));
        }

        // Apply search query
        if (searchQueryWO) {
            const query = searchQueryWO.toLowerCase();
            filtered = filtered.filter(wo =>
                wo.title?.toLowerCase().includes(query) ||
                wo.work_order_type?.toLowerCase().includes(query) ||
                wo.priority?.toLowerCase().includes(query) ||
                wo.description?.toLowerCase().includes(query)
            );
        }

        // Apply asset filter
        if (filterAssetWO) {
            filtered = filtered.filter(wo => wo.asset_name === filterAssetWO);
        }

        // Apply priority filter
        if (filterPriorityWO) {
            filtered = filtered.filter(wo => wo.priority === filterPriorityWO);
        }

        // Apply maintenance type filter
        if (filterMaintenanceTypeWO) {
            filtered = filtered.filter(wo => wo.work_order_type === filterMaintenanceTypeWO);
        }

        // Apply status filter
        if (filterStatusWO) {
            filtered = filtered.filter(wo => wo.status === filterStatusWO);
        }





        if (!Array.isArray(filtered)) return [];
        return [...filtered].sort((a, b) => {
            const dateA = new Date(a.created_at || a.CreatedAt || 0).getTime();
            const dateB = new Date(b.created_at || b.CreatedAt || 0).getTime();
            return dateB - dateA;
        });

    }, [allWorkOrders, selectedPlanId, searchQueryWO, filterAssetWO, filterPriorityWO, filterMaintenanceTypeWO, filterStatusWO]);

    //Update reference when selecting MaintPlan 
    useEffect(() =>
        setSelectedPlan(plans2.find(plan => plan.id === parseInt(selectedPlanId))),
        [selectedPlanId, plans2]);


    // Extract unique values for work orders filters
    const uniqueAssetsWO = useMemo(() => {
        const assets = [...new Set(allWorkOrders.map(wo => wo.asset_name).filter(Boolean))].sort();
        return assets;
    }, [allWorkOrders]);

    const uniquePrioritiesWO = useMemo(() => {
        const priorities = [...new Set(allWorkOrders.map(wo => wo.priority).filter(Boolean))].sort();
        return priorities;
    }, [allWorkOrders]);

    const uniqueMaintenanceTypesWO = useMemo(() => {
        const types = [...new Set(allWorkOrders.map(wo => wo.work_order_type).filter(Boolean))].sort();
        return types;
    }, [allWorkOrders]);

    const uniqueStatusesWO = useMemo(() => {
        const statuses = [...new Set(allWorkOrders.map(wo => wo.status).filter(Boolean))].sort();
        return statuses;
    }, [allWorkOrders]);

    // Pagination for work orders
    const totalWOTems = filteredWorkOrders.length;
    const totalWOPages = Math.max(1, Math.ceil(totalWOTems / woPageSize));
    const paginatedWorkOrders = useMemo(
        () => filteredWorkOrders.slice((woPage - 1) * woPageSize, woPage * woPageSize),
        [filteredWorkOrders, woPage, woPageSize]
    );
    const woRangeStart = totalWOTems === 0 ? 0 : (woPage - 1) * woPageSize + 1;
    const woRangeEnd = Math.min(woPage * woPageSize, totalWOTems);

    // Reset page when filters change
    useEffect(() => {
        setWoPage(1);
    }, [searchQueryWO, filterAssetWO, filterPriorityWO, filterMaintenanceTypeWO, selectedPlanId, woPageSize]);

    // Extract unique values for maintenance plan filters
    const uniqueAssets = useMemo(() => {
        const assets = [...new Set(plans2.map(p => p.asset_location).filter(Boolean))].sort();
        return assets;
    }, [plans2]);

    const uniquePriorities = useMemo(() => {
        const priorities = [...new Set(plans2.map(p => p.priority).filter(Boolean))].sort();
        return priorities;
    }, [plans2]);

    const uniqueMaintenanceTypes = useMemo(() => {
        const types = [...new Set(plans2.map(p => p.maintenance_type).filter(Boolean))].sort();
        return types;
    }, [plans2]);

    // Filter maintenance plans based on search query and filters
    const filteredPlans = useMemo(() => {
        let filtered = plans2;

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(plan =>
                plan.name?.toLowerCase().includes(query) ||
                plan.maintenance_type?.toLowerCase().includes(query) ||
                plan.priority?.toLowerCase().includes(query) ||
                plan.description?.toLowerCase().includes(query)
            );
        }

        if (filterAsset) {
            filtered = filtered.filter(plan => plan.asset_location === filterAsset);
        }

        if (filterPriority) {
            filtered = filtered.filter(plan => plan.priority === filterPriority);
        }

        if (filterMaintenanceType) {
            filtered = filtered.filter(plan => plan.maintenance_type === filterMaintenanceType);
        }

        return filtered;
    }, [plans2, searchQuery, filterAsset, filterPriority, filterMaintenanceType]);

    // Mutations
    //Create new work order
    const createMutation = useMutation({
        mutationFn: (productData) => workOrdersService.create(productData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders'] });
            setIsModalOpen(false);
        },
        onError: (error) => {
            console.error('Error creating plan:', error);
            throw error;
        },
    });

    //Update work order
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => workOrdersService.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders'] });
            queryClient.invalidateQueries({ queryKey: ['wo', selectedWoId] });
        },
        onError: (error) => {
            console.error('Error updating product:', error);
            throw error;
        },
    });

    //Delete work order
    const deleteMutation = useMutation({
        mutationFn: (id) => workOrdersService.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders'] });
            setWoToDelete(null);
            if (selectedWoId === woToDelete) {
                setSelectedWoId(null);
                setSelectedWO(null);
            }
        },
        onError: (error) => {
            console.error('Error deleting work order:', error);
            setWoToDelete(null);
            alert(error.message || 'Error deleting work order');
        },
    });

    // Handles
    //Open creation modal
    const handleCreate = () => {
        setSelectedWoId(null);
        setIsModalOpen(true);
    };

    //Call confirmation window before deleting WO
    const handleDelete = (productId) => {
        if (window.confirm('Are you sure you want to delete this work order? This action cannot be undone.')) {
            setWoToDelete(productId);
            deleteMutation.mutate(productId);
        }
    };

    //Activated when pressing save at Creation Modal activated from handleCreate()
    const handleSaveCreate = async (productData) => {
        await createMutation.mutateAsync(productData);
    };

    //Activated when pressing save at Edit Modal activated from handleEdit()
    const handleSaveUpdate = async (productData) => {
        if (selectedWoId) {
            await updateMutation.mutateAsync({
                id: selectedWoId,
                data: productData
            });
        }
    };

    //Handle WO duplication
    const handleSaveDuplicate = async (productData) => {
        await createMutation.mutateAsync(productData);
    };

    //Handle WO selection behaviour on click
    const handleProductClick = (product) => {
        setSelectedWoId(product.id);
    };

    //Loading/Error states fetching MaintPlans
    const isLoading = isLoadingPlans;
    const isError = isErrorPlans;
    const error = plansError;

    // Loading screen
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-400">Loading plans...</p>
                </div>
            </div>
        );
    }

    // Error screen
    if (isError) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-400 mb-2">Error loading plans</p>
                    <p className="text-gray-400 text-sm">{error?.message || 'Unknown error'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 w-full min-w-0">
            <div className="mb-6">
                <h1 className="text-3xl pl-6 font-bold text-white mb-2">Work Orders</h1>
                <p className="text-gray-400">Manage work orders and their specifications</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
                {/* Left Column: Work Order Table */}
                <div className="min-w-0 flex flex-col min-h-0">
                    <WorkOrdersTable
                        // Maintenance plan props
                        plans={filteredPlans}
                        // MaintPlan Filter Props
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        filterAsset={filterAsset}
                        setFilterAsset={setFilterAsset}
                        filterPriority={filterPriority}
                        setFilterPriority={setFilterPriority}
                        filterMaintenanceType={filterMaintenanceType}
                        setFilterMaintenanceType={setFilterMaintenanceType}
                        uniqueAssets={uniqueAssets}
                        uniquePriorities={uniquePriorities}
                        uniqueMaintenanceTypes={uniqueMaintenanceTypes}
                        //Create handler
                        onCreate={handleCreate}
                        filteredPlansCount={filteredPlans.length}

                        // Work orders props
                        workOrders={paginatedWorkOrders}
                        // Pagination props
                        totalItems={totalWOTems}
                        totalPages={totalWOPages}
                        rangeStart={woRangeStart}
                        rangeEnd={woRangeEnd}
                        page={woPage}
                        setPage={setWoPage}
                        pageSize={woPageSize}
                        setPageSize={setWoPageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        //WO Filter Props
                        searchQueryWO={searchQueryWO}
                        setSearchQueryWO={setSearchQueryWO}
                        filterAssetWO={filterAssetWO}
                        setFilterAssetWO={setFilterAssetWO}
                        filterPriorityWO={filterPriorityWO}
                        setFilterPriorityWO={setFilterPriorityWO}
                        filterMaintenanceTypeWO={filterMaintenanceTypeWO}
                        setFilterMaintenanceTypeWO={setFilterMaintenanceTypeWO}
                        filterStatusWO={filterStatusWO}
                        setFilterStatusWO={setFilterStatusWO}
                        uniqueAssetsWO={uniqueAssetsWO}
                        uniquePrioritiesWO={uniquePrioritiesWO}
                        uniqueMaintenanceTypesWO={uniqueMaintenanceTypesWO}
                        uniqueStatusesWO={uniqueStatusesWO}
                        //Selection props
                        selectedPlanId={selectedPlanId}
                        setSelectedPlanId={setSelectedPlanId}
                        isLoading={isLoadingWorkOrders}
                        onWorkOrderClick={handleProductClick}
                        selectedWorkOrderId={selectedWoId}
                    />
                </div>

                {/* Right Column: Work Order Details */}
                <div className="min-w-0 flex flex-col min-h-0 lg:sticky lg:top-6">
                    <PlansDetails
                        product={selectedWO}
                        mode='WO'
                        loading={isLoadingWo}
                        onDelete={handleDelete}
                        onSaveUpdate={handleSaveUpdate}
                        onSaveDuplicate={handleSaveDuplicate}
                        maintenancePlan={selectedPlan}
                    />
                </div>
            </div>

            {/* Work Order Creation Modal */}
            {isModalOpen && (
                <WorkOrdersModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        if (!selectedWoId) {
                            setSelectedWoId(null);
                        }
                    }}
                    maintenancePlan={selectedPlan}
                    onSave={handleSaveCreate}
                />
            )}
        </div>
    );
};

export default PlanesAsignacion;